import React from "react";
import { Box } from "@mui/material";
import { VoucherCSVRow } from "../../../../../models/Vouchers/VoucherEmailer";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";

interface VoucherEmailPreviewProps {
  logoUrl?: string | null;
  message: string;
  sampleVoucher: VoucherCSVRow;
  template?: string;
  hideSwageazyLogo?: boolean;
}

const DIWALI = {
  background: "#FEFBF6",
  plum: "#762768",
  codeInk: "#762768",
  cardBorder: "#E6DED3",
  divider: "#ECE7E1",
  serif: "'Times New Roman', Georgia, serif",
  sans: "'Poppins', 'Helvetica Neue', Arial, sans-serif",
  bannerUrl:
    "https://content.swageazy.com/emailers/Festive-Diwali-Garland-Banner.png?w=1200&h=328",
  giftCardUrl:
    "https://content.swageazy.com/emailers/Elegant-Diwali-Diya-Floral-Border-2.png?w=1200&h=400",
  footerUrl:
    "https://content.swageazy.com/emailers/Elegant-Diwali-Diya-Floral-Border.png?w=1200&h=328",
};

// Mirrors static/VoucherDiwaliEmailer.html. Art sizes are kept as aspect ratios so the
// preview scales with its container the same way the 600px email does.
const diwaliArt = (url: string) => ({
  backgroundImage: `url('${url}')`,
  backgroundSize: "100% 100%",
  backgroundRepeat: "no-repeat",
});

const DiwaliDetailRow = ({
  label,
  value,
  first,
}: {
  label: string;
  value: React.ReactNode;
  first?: boolean;
}) => (
  <Box
    sx={{
      padding: `${first ? 16 : 12}px 18px 10px`,
      borderBottom: `1px solid ${DIWALI.divider}`,
    }}
  >
    <Box
      sx={{
        fontFamily: DIWALI.sans,
        fontSize: "12px",
        color: DIWALI.plum,
        mb: 0.5,
      }}
    >
      {label}
    </Box>
    <Box sx={{ fontFamily: DIWALI.sans, fontSize: "16px", color: DIWALI.plum }}>
      {value}
    </Box>
  </Box>
);

const DiwaliEmailPreview = ({
  logoUrl,
  message,
  sampleVoucher,
  hideSwageazyLogo,
}: VoucherEmailPreviewProps) => (
  <Box
    sx={{
      backgroundColor: "#ffffff",
      paddingX: 2,
      paddingY: 1,
      borderRadius: 2,
      maxHeight: "500px",
      overflow: "auto",
      boxShadow: "inset 0 2px 8px rgba(0, 0, 0, 0.05)",
    }}
  >
    {!hideSwageazyLogo && (
      <Box sx={{ textAlign: "center", mb: 1 }}>
        <a
          href="https://www.swageazy.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="https://content.swageazy.com/logos/swageazy_logo_small.png"
            alt="Swageazy Logo"
            style={{ cursor: "pointer", height: "60px", width: "130px" }}
          />
        </a>
      </Box>
    )}
    <Box
      sx={{
        margin: "0 auto",
        maxWidth: "500px",
        overflow: "hidden",
        backgroundColor: DIWALI.background,
      }}
    >
      <Box
        sx={{
          ...diwaliArt(DIWALI.bannerUrl),
          aspectRatio: "1200 / 328",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pt: "12%",
          boxSizing: "border-box",
        }}
      >
        {logoUrl && (
          <img
            src={logoUrl}
            alt="Customer Logo"
            style={{
              display: "block",
              width: "auto",
              height: "auto",
              maxWidth: "34%",
              maxHeight: "55%",
              objectFit: "contain",
            }}
          />
        )}
      </Box>

      <Box sx={{ textAlign: "center", px: 3, color: DIWALI.plum }}>
        <Box
          sx={{
            fontFamily: DIWALI.sans,
            fontSize: "15px",
          }}
        >
          Wishing you and your loved ones a
        </Box>
        <Box
          sx={{
            fontFamily: "'Cormorant Garamond', 'Times New Roman', serif",
            fontSize: "52px",
            lineHeight: 1.4,
            fontWeight: 500,
          }}
        >
          Happy Diwali
        </Box>
      </Box>

      <Box
        sx={{
          fontFamily: DIWALI.sans,
          fontSize: "17px",
          lineHeight: 1.5,
          color: DIWALI.plum,
          textAlign: "center",
          // Same cap as the email's 320px, scaled to this 500px preview.
          maxWidth: "267px",
          mx: "auto",
          px: 3,
          pb: 1,
          "& p": { margin: 0 },
        }}
        dangerouslySetInnerHTML={{
          __html: message || "Your custom message will appear here...",
        }}
      />

      {/* Rows line up with the art: light strip, maroon band, white box, maroon band, light strip. */}
      <Box
        sx={{
          ...diwaliArt(DIWALI.giftCardUrl),
          aspectRatio: "3 / 1",
          my: 2.5,
          display: "grid",
          gridTemplateRows: "19fr 37fr 62fr 37fr 26fr",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            alignSelf: "center",
            fontFamily: DIWALI.sans,
            fontSize: "11px",
            lineHeight: 1.2,
            letterSpacing: "2px",
            textTransform: "uppercase",
            color: DIWALI.plum,
          }}
        >
          Your Gift Card Code
        </Box>
        <Box />
        <Box
          sx={{
            alignSelf: "center",
            fontFamily: "Consolas, 'Courier New', monospace",
            fontSize: "20px",
            fontWeight: 700,
            letterSpacing: "2px",
            color: DIWALI.codeInk,
          }}
        >
          {sampleVoucher["Gift Card Code"] || "SAMPLE-CODE-123"}
        </Box>
        <Box />
        <Box
          sx={{
            alignSelf: "center",
            fontFamily: DIWALI.sans,
            fontSize: "11px",
            letterSpacing: "1.5px",
            color: DIWALI.plum,
          }}
        >
          Valid until {sampleVoucher["Expiry Date"] || "31 Dec 2026"}
        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: "380px",
          margin: "0 auto 8px",
          backgroundColor: "#ffffff",
          border: `1px solid ${DIWALI.cardBorder}`,
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        <DiwaliDetailRow
          first
          label="Product"
          value={sampleVoucher["Product Name"] || "Sample Product"}
        />
        <DiwaliDetailRow
          label="Amount"
          value={`${sampleVoucher["Currency Code"] || "INR"} ${sampleVoucher.Amount || "500"}`}
        />
        <DiwaliDetailRow
          label="Reference ID"
          value={sampleVoucher["Reference ID"] || "REF-12345"}
        />
        <Box sx={{ textAlign: "center", padding: "14px 18px 16px" }}>
          <Box
            component="a"
            href={sampleVoucher["Term and condition"] || undefined}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontFamily: DIWALI.sans,
              fontSize: "13px",
              color: DIWALI.plum,
              textDecoration: "none",
            }}
          >
            View Terms & Conditions
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          ...diwaliArt(DIWALI.footerUrl),
          aspectRatio: "1200 / 328",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: "20%",
          pb: "2.5%",
          boxSizing: "border-box",
          fontFamily: DIWALI.sans,
          fontSize: "11px",
          lineHeight: 1.5,
          color: DIWALI.plum,
          textAlign: "center",
        }}
      >
        © 2026 SuperV Technologies Pvt Ltd. All rights reserved.
      </Box>
    </Box>
  </Box>
);

const VoucherEmailPreview: React.FC<VoucherEmailPreviewProps> = ({
  logoUrl,
  message,
  sampleVoucher,
  template = "classic",
  hideSwageazyLogo = false,
}) => {
  if (template === "diwali") {
    return (
      <DiwaliEmailPreview
        logoUrl={logoUrl}
        message={message}
        sampleVoucher={sampleVoucher}
        hideSwageazyLogo={hideSwageazyLogo}
      />
    );
  }

  if (template === "modern") {
    return (
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          paddingX: 2,
          paddingY: 1,
          borderRadius: 2,
          maxHeight: "500px",
          overflow: "auto",
          boxShadow: "inset 0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
      >
        {!hideSwageazyLogo && (
          <Box sx={{ textAlign: "center" }}>
            <a
              href="https://www.swageazy.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://content.swageazy.com/logos/swageazy_logo_small.png"
                alt="Swageazy Logo"
                style={{
                  cursor: "pointer",
                  height: "60px",
                  width: "130px",
                }}
              />
            </a>
          </Box>
        )}
        <Box
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            margin: "0 auto",
            maxWidth: "500px",
            overflow: "hidden",
          }}
        >
          {logoUrl && (
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                textAlign: "center",
                padding: "30px 20px",
              }}
            >
              <img
                src={logoUrl}
                alt="Customer Logo"
                style={{
                  maxHeight: "60px",
                  maxWidth: "120px",
                  objectFit: "contain",
                  filter: "brightness(0) invert(1)",
                }}
              />
            </Box>
          )}

          <Box
            sx={{
              padding: "30px",
              textAlign: "center",
            }}
          >
            <CustomTypography
              variant="heading4"
              sx={{
                color: "#667eea",
                mb: 2,
              }}
            >
              🎉 You've Got a Gift!
            </CustomTypography>

            <Box
              sx={{
                lineHeight: 1.6,
                color: "#333",
                textAlign: "left",
                mb: 3,
                "& p": {
                  margin: "0 0 1em 0",
                },
                "& p:last-child": {
                  marginBottom: 0,
                },
              }}
              dangerouslySetInnerHTML={{
                __html: message || "Your custom message will appear here...",
              }}
            />

            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "12px",
                padding: "25px",
                margin: "20px auto",
                maxWidth: "350px",
              }}
            >
              <CustomTypography
                variant="body2"
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  mb: 2,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                }}
              >
                Your Gift Card Code
              </CustomTypography>
              <Box
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "15px",
                  mb: 2,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: "#667eea",
                    fontFamily: "monospace",
                    fontSize: "22px",
                    fontWeight: 700,
                    letterSpacing: "2px",
                  }}
                >
                  {sampleVoucher["Gift Card Code"] || "SAMPLE-CODE-123"}
                </Box>
              </Box>
              <CustomTypography
                variant="body2"
                sx={{
                  color: "#fff",
                  fontSize: "12px",
                  opacity: 0.9,
                }}
              >
                Tap to copy • Valid until{" "}
                {sampleVoucher["Expiry Date"] || "31 Dec 2026"}
              </CustomTypography>
            </Box>

            <Box
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                padding: "20px",
                margin: "20px auto",
                maxWidth: "350px",
                textAlign: "left",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <CustomTypography
                    variant="body2"
                    sx={{
                      color: "#666",
                      fontSize: "11px",
                      fontWeight: 600,
                      mb: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Product
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    sx={{
                      color: "#333",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    {sampleVoucher["Product Name"] || "Sample Product"}
                  </CustomTypography>
                </Box>
                <Box>
                  <CustomTypography
                    variant="body2"
                    sx={{
                      color: "#666",
                      fontSize: "11px",
                      fontWeight: 600,
                      mb: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Amount
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    sx={{
                      color: "#667eea",
                      fontSize: "16px",
                      fontWeight: 700,
                    }}
                  >
                    {sampleVoucher["Currency Code"] || "INR"}{" "}
                    {sampleVoucher.Amount || "500"}
                  </CustomTypography>
                </Box>
              </Box>
              <Box sx={{ borderTop: "1px solid #e0e0e0", pt: 2 }}>
                <CustomTypography
                  variant="body2"
                  sx={{
                    color: "#666",
                    fontSize: "11px",
                    fontWeight: 600,
                    mb: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  Reference ID
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  sx={{
                    color: "#333",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  {sampleVoucher["Reference ID"] || "REF-12345"}
                </CustomTypography>
              </Box>
              {sampleVoucher["Term and condition"] && (
                <Box
                  sx={{
                    borderTop: "1px solid #e0e0e0",
                    mt: 2,
                    pt: 2,
                    textAlign: "center",
                  }}
                >
                  <Box
                    component="a"
                    href={sampleVoucher["Term and condition"]}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "#667eea",
                      fontSize: "13px",
                      fontWeight: 600,
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    View Terms & Conditions →
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: "#f8f9fa",
              color: "#999",
              padding: "20px",
              textAlign: "center",
              fontSize: "11px",
            }}
          >
            © 2021 SuperV Technologies Pvt Ltd. All rights reserved.
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#e5e4e2",
        paddingX: 2,
        paddingY: 1,
        borderRadius: 2,
        maxHeight: "500px",
        overflow: "auto",
        boxShadow: "inset 0 2px 8px rgba(0, 0, 0, 0.05)",
      }}
    >
      {!hideSwageazyLogo && (
        <Box sx={{ textAlign: "center" }}>
          <a
            href="https://www.swageazy.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="https://content.swageazy.com/logos/swageazy_logo_small.png"
              alt="Swageazy Logo"
              style={{
                cursor: "pointer",
                height: "60px",
                width: "130px",
              }}
            />
          </a>
        </Box>
      )}
      <Box
        sx={{
          backgroundColor: "#f0efec",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
          margin: "0 auto",
          maxWidth: "500px",
          padding: "0 20px",
        }}
      >
        {logoUrl && (
          <Box sx={{ textAlign: "center", padding: "20px" }}>
            <img
              src={logoUrl}
              alt="Customer Logo"
              style={{
                maxHeight: "60px",
                maxWidth: "120px",
                objectFit: "contain",
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: "#fff",
            padding: "20px 30px",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              lineHeight: 1.5,
              color: "#000",
              fontWeight: 500,
              textAlign: "left",
              "& p": {
                margin: "0 0 1em 0",
              },
              "& p:last-child": {
                marginBottom: 0,
              },
            }}
            dangerouslySetInnerHTML={{
              __html: message || "Your custom message will appear here...",
            }}
          />

          <Box
            sx={{
              backgroundColor: "#f8f8f8",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              margin: "20px auto",
              padding: "15px",
              textAlign: "center",
              maxWidth: "300px",
            }}
          >
            <CustomTypography
              variant="body2"
              sx={{
                color: "#333",
                fontSize: "16px",
                fontWeight: 600,
                marginBottom: "10px",
              }}
            >
              Your Voucher Code
            </CustomTypography>
            <Box
              sx={{
                backgroundColor: "#fff",
                border: "1px dashed #ccc",
                borderRadius: "4px",
                display: "inline-block",
                marginBottom: "10px",
                padding: "8px 15px",
              }}
            >
              <Box
                component="span"
                sx={{
                  color: "#003599",
                  fontFamily: "monospace",
                  fontSize: "18px",
                  fontWeight: 600,
                  letterSpacing: "1px",
                }}
              >
                {sampleVoucher["Gift Card Code"] || "SAMPLE-CODE-123"}
              </Box>
            </Box>
            <Box sx={{ marginTop: "10px" }}>
              <CustomTypography
                variant="body2"
                sx={{
                  color: "#666",
                  fontSize: "12px",
                  margin: "0 0 5px 0",
                }}
              >
                Tap and hold to copy the code
              </CustomTypography>
              <CustomTypography
                variant="body2"
                sx={{
                  color: "#666",
                  fontSize: "12px",
                  margin: 0,
                }}
              >
                or select and copy manually
              </CustomTypography>
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: "#f8f8f8",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              margin: "20px auto",
              padding: "15px",
              textAlign: "left",
              maxWidth: "300px",
            }}
          >
            <Box sx={{ marginBottom: "10px" }}>
              <Box
                component="span"
                sx={{
                  color: "#666",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Product:
              </Box>
              <Box
                component="span"
                sx={{
                  color: "#333",
                  fontSize: "13px",
                  fontWeight: 600,
                  marginLeft: "5px",
                }}
              >
                {sampleVoucher["Product Name"] || "Sample Product"}
              </Box>
            </Box>
            <Box sx={{ marginBottom: "10px" }}>
              <Box
                component="span"
                sx={{
                  color: "#666",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Amount:
              </Box>
              <Box
                component="span"
                sx={{
                  color: "#333",
                  fontSize: "13px",
                  fontWeight: 600,
                  marginLeft: "5px",
                }}
              >
                {sampleVoucher["Currency Code"] || "INR"}{" "}
                {sampleVoucher.Amount || "500"}
              </Box>
            </Box>
            <Box sx={{ marginBottom: "10px" }}>
              <Box
                component="span"
                sx={{
                  color: "#666",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Reference ID:
              </Box>
              <Box
                component="span"
                sx={{
                  color: "#333",
                  fontSize: "13px",
                  fontWeight: 600,
                  marginLeft: "5px",
                }}
              >
                {sampleVoucher["Reference ID"] || "REF-12345"}
              </Box>
            </Box>
            <Box sx={{ marginBottom: "10px" }}>
              <Box
                component="span"
                sx={{
                  color: "#666",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Expiry Date:
              </Box>
              <Box
                component="span"
                sx={{
                  color: "#333",
                  fontSize: "13px",
                  fontWeight: 600,
                  marginLeft: "5px",
                }}
              >
                {sampleVoucher["Expiry Date"] || "31 Dec 2026"}
              </Box>
            </Box>
            {sampleVoucher["Term and condition"] && (
              <Box
                sx={{
                  borderTop: "1px solid #e0e0e0",
                  marginTop: "15px",
                  paddingTop: "15px",
                  textAlign: "center",
                }}
              >
                <Box
                  component="a"
                  href={sampleVoucher["Term and condition"]}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#003599",
                    fontSize: "13px",
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  View Terms & Conditions
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            color: "#838383",
            padding: "20px",
            textAlign: "center",
            fontSize: "12px",
          }}
        >
          © 2021 SuperV Technologies Pvt Ltd. All rights reserved.
        </Box>
      </Box>
    </Box>
  );
};

export default VoucherEmailPreview;
